import React from 'react';
import PropTypes from 'prop-types';
import { COMMERCIAL_SUPPLIER_SORT_DIRECTIONS } from './constants';
import { getNextDetailSortState } from './SubstanceCommercialSupplierInfo.helpers';
import { DETAIL_TABLE_COLUMNS, getHeaderColumnClassName } from './SubstanceCommercialSupplierDetailTable.helpers';
import SubstanceCommercialSupplierPriceSortTooltip from './SubstanceCommercialSupplierPriceSortTooltip';
import SubstanceCommercialSupplierSortHeaderButton, {
  getCommercialSupplierAriaSort,
  getCommercialSupplierSortAnnouncement,
} from './SubstanceCommercialSupplierSortHeaderButton';

function DetailTableHeaderLabel({ children, accent = false }) {
  return <span className={`rcs-commercial-details-table__header-label ${accent ? 'is-accent' : ''}`}>{children}</span>;
}

DetailTableHeaderLabel.propTypes = {
  children: PropTypes.node.isRequired,
  accent: PropTypes.bool,
};

function DetailPriceHeaderLabel({ children, tooltipId }) {
  return (
    <span className="rcs-commercial-details-table__price-header">
      <DetailTableHeaderLabel>{children}</DetailTableHeaderLabel>
      <SubstanceCommercialSupplierPriceSortTooltip id={tooltipId} />
    </span>
  );
}

DetailPriceHeaderLabel.propTypes = {
  children: PropTypes.node.isRequired,
  tooltipId: PropTypes.string.isRequired,
};

function getDetailAriaSort(columnValue, isPriceSortable, sortState) {
  const isSortable = columnValue === DETAIL_TABLE_COLUMNS.packageSize || (columnValue === DETAIL_TABLE_COLUMNS.price && isPriceSortable);
  const isActive = isSortable && sortState.field === columnValue && sortState.direction !== COMMERCIAL_SUPPLIER_SORT_DIRECTIONS.neutral;

  return getCommercialSupplierAriaSort(isActive, sortState.direction);
}

function SubstanceCommercialSupplierDetailTableHead({
  tableColumns,
  handleSort,
  isPriceSortable,
  onSortAnnouncement,
  priceSortTooltipId,
  sortState,
}) {
  const handleSortWithAnnouncement = (field) => {
    const nextSortState = getNextDetailSortState(sortState, field);
    const columnLabel = tableColumns.find((column) => column.value === field)?.label || field;

    onSortAnnouncement(getCommercialSupplierSortAnnouncement(columnLabel, nextSortState.direction));
    handleSort(field);
  };

  return (
    <tr className="c-els-table__row c-els-table__row--head">
      {tableColumns.map((column) => {
        const columnClassName = getHeaderColumnClassName(column.value);
        const isPackageSizeSortActive =
          sortState.field === DETAIL_TABLE_COLUMNS.packageSize && sortState.direction !== COMMERCIAL_SUPPLIER_SORT_DIRECTIONS.neutral;
        const isPriceSortActive =
          sortState.field === DETAIL_TABLE_COLUMNS.price && sortState.direction !== COMMERCIAL_SUPPLIER_SORT_DIRECTIONS.neutral;

        return (
          <th
            key={column.value}
            scope="col"
            aria-sort={getDetailAriaSort(column.value, isPriceSortable, sortState)}
            className={`${columnClassName} c-els-table__cell c-els-table__cell--header`}>
            {column.value === DETAIL_TABLE_COLUMNS.packageSize && (
              <SubstanceCommercialSupplierSortHeaderButton
                dataE2e="commercial-supplier-detail-sort-packageSize"
                direction={sortState.direction}
                isActive={isPackageSizeSortActive}
                label={column.label}
                className="rcs-commercial-sort-button--detail is-accent"
                onSort={() => handleSortWithAnnouncement(DETAIL_TABLE_COLUMNS.packageSize)}
              />
            )}
            {column.value === DETAIL_TABLE_COLUMNS.price &&
              (isPriceSortable ? (
                <SubstanceCommercialSupplierSortHeaderButton
                  dataE2e="commercial-supplier-detail-sort-price"
                  direction={sortState.direction}
                  isActive={isPriceSortActive}
                  label={column.label}
                  className="rcs-commercial-sort-button--detail is-accent"
                  onSort={() => handleSortWithAnnouncement(DETAIL_TABLE_COLUMNS.price)}
                />
              ) : (
                <DetailPriceHeaderLabel tooltipId={priceSortTooltipId}>{column.label}</DetailPriceHeaderLabel>
              ))}
            {column.value !== DETAIL_TABLE_COLUMNS.packageSize && column.value !== DETAIL_TABLE_COLUMNS.price && column.label && (
              <DetailTableHeaderLabel>{column.label}</DetailTableHeaderLabel>
            )}
          </th>
        );
      })}
    </tr>
  );
}

SubstanceCommercialSupplierDetailTableHead.propTypes = {
  tableColumns: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.string.isRequired,
    })
  ).isRequired,
  handleSort: PropTypes.func.isRequired,
  isPriceSortable: PropTypes.bool.isRequired,
  onSortAnnouncement: PropTypes.func.isRequired,
  priceSortTooltipId: PropTypes.string.isRequired,
  sortState: PropTypes.shape({
    field: PropTypes.string,
    direction: PropTypes.string,
  }).isRequired,
};

export default SubstanceCommercialSupplierDetailTableHead;
